import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ClassVmCostReport,
  IClassesEntityService,
} from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { ExportService } from '../../../../../../services/export.service';

/**
 * Top-level Cost Analysis tab for the class detail page.
 *
 * Owns the VM cost report state and exposes it to the inner "User VM"
 * sub-tab ({@link CostAnalysisSubTabComponent}) via template bindings.
 */
@Component({
  selector: 'app-cost-analysis-tab',
  templateUrl: './cost-analysis-tab.component.html',
  styleUrls: ['./cost-analysis-tab.component.css'],
})
export class CostAnalysisTabComponent implements OnInit, OnDestroy {
  /** ID of the class whose cost data is being displayed. */
  @Input() classId!: string;

  /** Display title of the class — used as the Excel sheet name. */
  @Input() classTitle = '';

  /** Per-participant RG-scoped VM cost report, populated on first load request. */
  vmCostReport: ClassVmCostReport | null = null;
  isLoadingVmCostReport = false;
  vmCostReportError: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    readonly exportService: ExportService,
  ) { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Fetches the per-participant VM cost report.
   * Called lazily when the user first interacts with the sub-tab.
   */
  loadVmCostReport(): void {
    if (this.isLoadingVmCostReport) return;
    this.isLoadingVmCostReport = true;
    this.vmCostReportError = null;
    this.classService.getClassVmCostReport(this.classId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (report: ClassVmCostReport) => {
          this.vmCostReport = report;
          this.isLoadingVmCostReport = false;
        },
        error: (err: any) => {
          this.vmCostReportError = err?.error?.message ?? 'Failed to load VM cost report.';
          this.isLoadingVmCostReport = false;
        },
      });
  }
}
